interface WeatherIconResult {
  src: string;  // e.g. '/weather-icons/rain.svg?v=2'
}

// Bump this when weather icon files change to bust browser/CDN caches.
// Files in public/ keep the same URL across deploys, so browsers that
// cached the old version (with a long max-age) won't re-fetch without this.
export const WEATHER_ICON_VERSION = 3;

/**
 * Maps NWS shortForecast text to a Meteocons SVG path.
 * Falls back to clear-day/clear-night based on isDaytime.
 */
export function getWeatherIcon(shortForecast: string | null, isDaytime: boolean): WeatherIconResult {
  const icon = (name: string) => ({ src: `/weather-icons/${name}.svg?v=${WEATHER_ICON_VERSION}` });

  if (!shortForecast) {
    return icon(isDaytime ? 'clear-day' : 'clear-night');
  }

  const text = shortForecast.toLowerCase();

  // Thunderstorm
  if (text.includes('thunder') || text.includes('tstorm') || text.includes('t-storm')) {
    return icon(isDaytime ? 'thunderstorms-day' : 'thunderstorms-night');
  }

  // Snow / Ice / Sleet / Freezing
  if (text.includes('snow') || text.includes('blizzard')) return icon('snow');
  if (text.includes('ice') || text.includes('sleet') || text.includes('freezing')) return icon('sleet');

  // Rain / Showers
  if (text.includes('rain') || text.includes('shower')) return icon('rain');

  // Drizzle
  if (text.includes('drizzle')) return icon('drizzle');

  // Fog
  if (text.includes('fog')) return icon('fog');

  // Haze / Mist
  if (text.includes('haze') || text.includes('mist')) {
    return icon(isDaytime ? 'haze-day' : 'haze-night');
  }

  // Overcast / Cloudy (fully)
  if (text.includes('overcast') || (text.includes('cloudy') && !text.includes('partly') && !text.includes('mostly clear'))) {
    return icon(isDaytime ? 'overcast-day' : 'overcast-night');
  }

  // Partly cloudy / Mostly cloudy
  if (text.includes('partly') || text.includes('mostly cloudy')) {
    return icon(isDaytime ? 'partly-cloudy-day' : 'partly-cloudy-night');
  }

  // Clear / Sunny / Fair
  if (text.includes('clear') || text.includes('sunny') || text.includes('fair') || text.includes('mostly clear')) {
    return icon(isDaytime ? 'clear-day' : 'clear-night');
  }

  // Windy (no precip)
  if (text.includes('wind') && !text.includes('rain') && !text.includes('snow')) return icon('wind');

  // Fallback: clear day/night
  return icon(isDaytime ? 'clear-day' : 'clear-night');
}

interface WeatherGradientResult {
  gradient: string;
  textColor: string; // 'white' for dark gradients, dark for light ones
}

/**
 * Maps NWS shortForecast text to a CSS gradient + text color for pill display.
 */
export function getWeatherGradient(shortForecast: string | null, isDaytime: boolean): WeatherGradientResult {
  if (!shortForecast) {
    return isDaytime
      ? { gradient: 'linear-gradient(135deg, #FF9800, #FFB74D)', textColor: '#fff' }
      : { gradient: 'linear-gradient(135deg, #1A237E, #3949AB)', textColor: '#fff' };
  }

  const text = shortForecast.toLowerCase();

  if (text.includes('thunder') || text.includes('tstorm') || text.includes('t-storm')) {
    return { gradient: 'linear-gradient(135deg, #4A148C, #7B1FA2)', textColor: '#fff' };
  }
  if (text.includes('snow') || text.includes('ice') || text.includes('sleet') || text.includes('freezing') || text.includes('blizzard')) {
    return { gradient: 'linear-gradient(135deg, #B3E5FC, #E1F5FE)', textColor: 'rgba(0,0,0,0.8)' };
  }
  if (text.includes('rain') || text.includes('shower') || text.includes('drizzle')) {
    return { gradient: 'linear-gradient(135deg, #1565C0, #42A5F5)', textColor: '#fff' };
  }
  if (text.includes('fog') || text.includes('haze') || text.includes('mist')) {
    return { gradient: 'linear-gradient(135deg, #CFD8DC, #ECEFF1)', textColor: 'rgba(0,0,0,0.8)' };
  }
  if (text.includes('overcast') || (text.includes('cloudy') && !text.includes('partly') && !text.includes('mostly clear'))) {
    return { gradient: 'linear-gradient(135deg, #78909C, #B0BEC5)', textColor: '#fff' };
  }
  if (text.includes('partly') || text.includes('mostly cloudy')) {
    return { gradient: 'linear-gradient(135deg, #64B5F6, #90CAF9)', textColor: '#fff' };
  }
  if (text.includes('clear') || text.includes('sunny') || text.includes('fair') || text.includes('mostly clear')) {
    return isDaytime
      ? { gradient: 'linear-gradient(135deg, #FF9800, #FFB74D)', textColor: '#fff' }
      : { gradient: 'linear-gradient(135deg, #1A237E, #3949AB)', textColor: '#fff' };
  }

  // Fallback
  return isDaytime
    ? { gradient: 'linear-gradient(135deg, #FF9800, #FFB74D)', textColor: '#fff' }
    : { gradient: 'linear-gradient(135deg, #1A237E, #3949AB)', textColor: '#fff' };
}

/**
 * Get color for AQI value.
 */
export function getAqiColor(aqi: number): string {
  if (aqi <= 50) return '#4CAF50';   // green — Good
  if (aqi <= 100) return '#FF9800';  // orange — Moderate
  return '#F44336';                   // red — Unhealthy+
}
